import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import LegalFooter from '../components/LegalFooter';

const AUTHORITY_SITE = 'https://aiaccountabilitycouncil.com';

const faqData = [
  {
    category: "Offerings",
    questions: [
      {
        q: "What is the AASC Accountability Declaration?",
        a: "The AASC Accountability Declaration ($997/year) is a public commitment to documented AI governance. It confirms that an organization has structured accountability practices within a defined scope. Declaration is not verification — it is a first-party statement of documented controls."
      },
      {
        q: "What does accountability declaration represent?",
        a: "Accountability declaration confirms documented governance alignment with AASC foundational control standards at a point in time. Declaration is conditional and revocable. It does not guarantee performance, imply regulatory approval, or transfer liability."
      },
      {
        q: "How long is a declaration valid?",
        a: "All declarations are valid for 12 months from the date of issuance. Organizations must renew annually to maintain active status."
      },
      {
        q: "What standards are included?",
        a: "Declarations include alignment against all six AASC 2.0 Foundational Control Standards: AI-HITL-1.0 (Human Authority and Escalation), AI-ACCT-1.0 (Outcome Accountability Mapping), AI-READ-1.0 (Readiness and Incident Response), AI-PERF-1.0 (Performance Monitoring and Change Control), AI-ROI-1.0 (Responsibility Domain Mapping), and AI-DISC-1.0 (Client Disclosure and Transparency). Full definitions are published on the authority site."
      },
      {
        q: "Is declaration an endorsement?",
        a: "No. Accountability declaration does not constitute an endorsement, approval, or recommendation. It indicates documented conformance with published standards based on evidence submitted at the time of application."
      },
      {
        q: "Can declaration be revoked?",
        a: "Yes. The Council may revoke declaration for misrepresentation, non-compliance, or other reasons. Revoked declarations remain visible in the public Registry with 'Revoked' status."
      }
    ]
  },
  {
    category: "Application Process",
    questions: [
      {
        q: "How do I apply?",
        a: "Organizations may apply through this Registry Platform by submitting organization details and documentation, completing payment, and accepting required acknowledgments."
      },
      {
        q: "What are the fees?",
        a: "AASC Accountability Declaration is $997 USD per year. Renewal is at the same rate annually."
      },
      {
        q: "What documentation is required?",
        a: "Accountability Declaration requires: defined scope statement, responsibility domain mapping (AI-ROI-1.0), human escalation structure (AI-HITL-1.0), incident response documentation (AI-READ-1.0), change control documentation (AI-PERF-1.0), and client disclosure acknowledgment (AI-DISC-1.0)."
      },
      {
        q: "What is the AI Accountability Documentation System?",
        a: "The AI Accountability Documentation System ($97) provides eight governance templates ready to customize and use. It helps organizations prepare for declaration by creating the required documentation."
      },
      {
        q: "Can I appeal a declaration decision?",
        a: "Declaration decisions are final. The Council is under no obligation to provide an appeal process."
      }
    ]
  },
  {
    category: "Registry & Verification",
    questions: [
      {
        q: "What information is displayed in the public Registry?",
        a: "The public Registry displays organization name, declaration status (Declared, Conditionally Declared, Revoked, Expired), effective date, expiration date, and scope statement."
      },
      {
        q: "What are the status labels?",
        a: "Status labels are defined by the AI Accountability Standards Council: Accountability Declared, Conditionally Declared, Revoked, Expired. No additional categories are used."
      },
      {
        q: "How can I verify a declaration?",
        a: "Third parties may verify status by entering the declaration ID on the Registry verification page."
      },
      {
        q: "Are expired or revoked entries removed?",
        a: "No. The Registry maintains a record of all entries. Expired and revoked entries remain visible with their status clearly indicated."
      }
    ]
  },
  {
    category: "Authority & Governance",
    questions: [
      {
        q: "What is the relationship between this platform and the AI Accountability Standards Council?",
        a: "This platform administers AASC Accountability Declarations in accordance with the published standards of the AI Accountability Standards Council. The authority site defines standards; this platform executes process."
      },
      {
        q: "Where can I find the official standards?",
        a: "Official standards, governance policies, and definitions are published on the AI Accountability Standards Council authority site (aiaccountabilitycouncil.com). This platform does not define or restate standards."
      },
      {
        q: "Is this platform a regulatory body?",
        a: "No. This platform is not a regulatory body. The AI Accountability Standards Council publishes voluntary technical standards. Declaration does not imply regulatory compliance."
      }
    ]
  },
  {
    category: "Privacy & Data",
    questions: [
      {
        q: "What data is collected?",
        a: "The Registry Platform collects organization information, user account credentials, application documentation, and payment records. See the Privacy Policy for complete details."
      },
      {
        q: "Is personal data sold?",
        a: "No. Personal information is not sold, rented, or traded to third parties."
      },
      {
        q: "Where can I find the Terms and Privacy Policy?",
        a: "Terms of Service and Privacy Policy are available on the authority site (aiaccountabilitycouncil.com). This platform mirrors that text with additional Registry Platform Operational Terms."
      }
    ]
  }
];

export default function FAQ() {
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="faq-page">
      <header className="border-b border-zinc-200">
        <div className="container mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <Link to="/" className="header-logo text-xl">AASC Registry</Link>
              <a 
                href={AUTHORITY_SITE}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1 border-l border-zinc-200 pl-6"
              >
                AI Accountability Standards Council <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <nav className="flex items-center gap-6">
              <Link to="/registry" className="nav-link">Registry</Link>
              <Link to="/apply" className="nav-link">Apply</Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-8 py-12 flex-1">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Frequently Asked Questions
          </h1>
          <p className="text-zinc-500 mb-8">AASC Registry Platform</p>

          <div className="space-y-8">
            {faqData.map((section, sectionIndex) => (
              <div key={sectionIndex}>
                <h2 className="text-lg font-semibold text-zinc-900 mb-4 pb-2 border-b border-zinc-200">
                  {section.category}
                </h2>
                <Accordion type="single" collapsible className="space-y-2">
                  {section.questions.map((item, itemIndex) => (
                    <AccordionItem 
                      key={itemIndex} 
                      value={`${sectionIndex}-${itemIndex}`}
                      className="border border-zinc-200 px-4"
                    >
                      <AccordionTrigger className="text-left hover:no-underline py-4">
                        <span className="font-medium text-zinc-900">{item.q}</span>
                      </AccordionTrigger>
                      <AccordionContent className="text-zinc-600 pb-4">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>

          {/* Authority Reference */}
          <div className="mt-12 p-6 bg-zinc-50 border border-zinc-200">
            <h3 className="font-semibold text-zinc-900 mb-2">Official Resources</h3>
            <p className="text-zinc-600 text-sm mb-4">
              Standards, governance policies, and definitions are published on the official authority site.
            </p>
            <div className="flex flex-wrap gap-4 text-sm">
              <a 
                href={`${AUTHORITY_SITE}/standards`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-900 underline flex items-center gap-1"
              >
                Standards Index <ExternalLink className="w-3 h-3" />
              </a>
              <a 
                href={`${AUTHORITY_SITE}/governance-and-authority`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-900 underline flex items-center gap-1"
              >
                Governance <ExternalLink className="w-3 h-3" />
              </a>
              <a 
                href={`${AUTHORITY_SITE}/terms-of-service`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-900 underline flex items-center gap-1"
              >
                Terms of Service <ExternalLink className="w-3 h-3" />
              </a>
              <a 
                href={`${AUTHORITY_SITE}/privacy-policy`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-900 underline flex items-center gap-1"
              >
                Privacy Policy <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          {/* Contact */}
          <div className="mt-8 pt-8 border-t border-zinc-200 text-sm text-zinc-600">
            <p>
              For questions not covered above, contact{' '}
              <a href="mailto:contact@aiaccountabilitycouncil.com" className="text-zinc-900 underline">
                contact@aiaccountabilitycouncil.com
              </a>
            </p>
          </div>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
